import { useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import type { AgGridReact } from 'ag-grid-react';
import type { OutletContext } from './pageTypes';
import {
  keywordsColumnConfig,
  keywordsColumnConfigSec
} from '@/app/KeywordTracker/config/keywordsColumnConfig';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import keywordsApi, {
  useGetKeywordsFuncMutation,
  useGetKeywordsQuery,
  useGetLandingUrlsForSelectQuery
} from '@/app/KeywordTracker/api/keywordsApi';
import { useLocation, useOutletContext, useSearchParams } from 'react-router-dom';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import AgGridWrapperTracker from '@/app/common/components/ag-grid/AgGridWrapperTracker';
import { useDispatch, useSelector } from 'react-redux';
import { useExportTableMutation } from '@/app/KeywordTracker/api/exportApi';
import { useGetTableSettingssQuery } from '@/app/common/api/tableSettingApi';
import { convertToQueryString } from '@/utils/queryString';
import {
  intentMap,
  purposeMap
} from '@/app/KeywordResearch/config/keywordGapColumnConfig';
import { useGetGroupsForSelectQuery } from '../api/groupsApi';
import { useGetTagsForSelectQuery } from '../api/tagsApi';
import { useGetLocationsForSelectQuery } from '../api/locationsApi';
import useDomain from '@/hooks/useDomain';
import KeywordsCharts from '../components/keywords/charts';
import KeywordsTimePicker from '../components/keywords/KeywordsTimePicker';
import KeywordsAccessAlerts from '../components/keywords/KeywordsAccessAlerts';
import GridActionButtons from '../components/keywords/GridActionButtons';
import AddUpdateButtons from '../components/keywords/AddUpdateButtons';
import UpdateProgressBar from '../components/keywords/UpdateProgressBar';
import KeywordSearchVolumeDrawer from '../components/keywords/drawers/KeywordSearchVolumeDrawer';
import FiltersComponent from '@/app/common/components/filters/FiltersComponent';
import FiltersComponentSkeleton from '@/app/common/components/filters/FiltersComponentSkeleton';
import DataLoadError from '@/components/errors/DataLoadError';
import {
  shouldShowDataLoadError,
  isNetworkApiError
} from '@/app/common/utils/apiErrorUtils';

type SortState = { column: string; order: string | null } | undefined;

const KeywordsPage = () => {
  const gridRef = useRef<AgGridReact | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [sort, setSort] = useState<SortState>();
  const [skipGetData, setSkipGetData] = useState(true);
  const { pathname } = useLocation();
  const { domain_slug } = useDomain();
  const [isOnUpdate, setIsOnUpdate] = useState(false);
  useGetUserInfoQuery();
  const [exportTable] = useExportTableMutation();
  const filters = useSelector(state => state.filters);

  const { data: tableSettings, isFetching: isLoadingSettings } =
    useGetTableSettingssQuery({
      table_name: pathname.split('/').slice(1, 3).join('/'),
      domain_slug: domain_slug
    });

  useEffect(() => {
    if (!isLoadingSettings) {
      if (tableSettings?.data) {
        const filters = tableSettings?.data.filters;
        const sortResult = tableSettings?.data.columns.find(
          item => item.sort !== null
        );
        if (sortResult) {
          setSort({
            column: sortResult.colId,
            order: sortResult.sort
          });
        }
        if (
          Object.keys(filters) !== 0 &&
          Object.keys(filters).length !== 0 &&
          !location.search.includes('FBy')
        ) {
          const queryString = convertToQueryString(filters);
          setSearchParams(queryString);
          window.location.reload();
        }
      }
      setSkipGetData(false);
    }
  }, [isLoadingSettings]);

  const [getKeywordsFunc] = useGetKeywordsFuncMutation();
  const [, setBreadcrumbData] = useOutletContext<OutletContext>();
  const dispatch = useDispatch();
  useEffect(() => {
    setBreadcrumbData([{ text: 'مدیریت کلمات', href: '#' }]);
  }, [setBreadcrumbData]);

  useEffect(() => {
    return () => {
      dispatch(keywordsApi.util.invalidateTags(['keywordsPage']));
    };
  }, [dispatch, pathname]);
  // SELECTORS
  const { data: groups, isFetching: isFetchingGroups } =
    useGetGroupsForSelectQuery(domain_slug);

  const { data: tags, isFetching: isFetchingTags } =
    useGetTagsForSelectQuery(domain_slug);

  const { data: locations, isLoading: isLoadingLocations } =
    useGetLocationsForSelectQuery();
  const { data: landings } = useGetLandingUrlsForSelectQuery(domain_slug);

  const { data, isLoading, isError, error, refetch, isFetching } =
    useGetKeywordsQuery(
      {
        from_date: Number(searchParams.get('from_date')),
        to_date: Number(searchParams.get('to_date')),
        page: 1,
        perPage: 50,
        filters,
        sort,
        domain_slug
      },
      { skip: skipGetData }
    );
  // این استیت برای این تعریف شده که وقتی کاربر به صفحه اول برمیگرده، جدول دوباره بارگذاری بشه
  const [shouldRevalidateFirstPage, setShouldRevalidateFirstPage] =
    useState(false);

  return (
    <>
      <KeywordSearchVolumeDrawer />

      <Row className="g-0 my-3 p-0 justify-content-between">
        <KeywordsAccessAlerts />
        {/* <IconAlert variant="warning" className="my-2">
          به دلیل قطع بودن اینترنت بین الملل این بخش در حال حاضر غیرفعال می
          باشد. برای دریافت جدیدترین اطلاعیه ها عضو{' '}
          <a
            href="https://ble.ir/mizfatools"
            style={{ textDecoration: 'underline' }}
            target="_blank"
            rel="noopener noreferrer"
          >
            کانال بله
          </a>{' '}
          شوید.
        </IconAlert> */}
        {/* حین اضافه کردن کلمه */}
        {/*  وقتی setShouldRevalidateFirstPage به true تغییر کرد، جدول دوباره بارگذاری میشه  (توی خود ag grid wrappper tracker وقتی به صفحه یک رفتیم)*/}
        <AddUpdateButtons
          setShouldRevalidateFirstPage={setShouldRevalidateFirstPage}
          gridRef={gridRef}
          isOnUpdate={isOnUpdate}
          setIsOnUpdate={setIsOnUpdate}
        />

        <KeywordsTimePicker
          show={!isLoading}
          from_date={data?.data?.from_date}
          to_date={data?.data?.to_date}
        />
      </Row>
      <Row>
        <Col xxl={12}>
          {isOnUpdate && (
            <UpdateProgressBar
              isOnUpdate={isOnUpdate}
              setIsOnUpdate={setIsOnUpdate}
            />
          )}
        </Col>
      </Row>
      <Row>
        {!isLoading &&
        !isLoadingSettings &&
        !isFetchingGroups &&
        !isFetchingTags &&
        !isLoadingLocations ? (
          <Col xxl={12}>
            {
              <FiltersComponent
                placement="left"
                preQueries={
                  searchParams.get('from_date') || searchParams.get('to_date')
                    ? `from_date=${searchParams.get('from_date')}&to_date=${searchParams.get('to_date')}`
                    : ''
                }
                columns={keywordsColumnConfigSec}
                selectItems={{
                  device_type: [
                    { value: 1, label: 'رایانه' },
                    { value: 2, label: 'گوشی' }
                  ],
                  tags: tags?.data,
                  group_title: groups?.data,
                  location: locations?.data,
                  landing_url: landings?.data,
                  purpose: Object.entries(purposeMap).map(([k, v]) => ({
                    label: v,
                    value: k
                  })),
                  intent: Object.entries(intentMap).map(([k]) => ({
                    label: k,
                    value: k
                  }))
                }}
              />
            }
          </Col>
        ) : (
          <Col xxl={12} className="mb-2">
            <FiltersComponentSkeleton />
          </Col>
        )}
      </Row>
      <KeywordsCharts domain_slug={domain_slug} />
      <Row>
        {shouldShowDataLoadError(isError, error) ? (
          <Col>
            <DataLoadError
              error={error}
              onRetry={() => refetch()}
              isRetrying={isFetching}
              isNetworkError={isNetworkApiError(error)}
            />
          </Col>
        ) : !isLoading && !isLoadingSettings && !skipGetData ? (
          <Col>
            <div>
              <AgGridWrapperTracker
                // ارسال Prop های استیت به داخل جدوول
                setShouldRevalidateFirstPage={setShouldRevalidateFirstPage}
                shouldRevalidateFirstPage={shouldRevalidateFirstPage}
                ref={gridRef}
                otherButtons={
                  // حین حذف کردن کلمه
                  //  وقتی setShouldRevalidateFirstPage به true تغییر کرد، جدول دوباره بارگذاری میشه  (توی خود ag grid wrappper tracker وقتی به صفحه یک رفتیم)
                  <GridActionButtons
                    setShouldRevalidateFirstPage={setShouldRevalidateFirstPage}
                    gridRef={gridRef}
                  />
                }
                table_name={location.pathname.split('/').slice(1, 3).join('/')}
                settings={{
                  pagination: true,
                  fullscreen: true,
                  changeHeight: true,
                  resetGrid: true,
                  saveGrid: true,
                  sideBar: true,
                  columnsState: tableSettings?.data,
                  export: {
                    enabled: true,
                    fetchFunction: exportTable
                  }
                }}
                columns={keywordsColumnConfig(
                  tags?.data,
                  groups?.data,
                  landings?.data,
                  location?.data
                )}
                total={data?.data?.total}
                additionalData={{
                  domain_slug,
                  from_date: Number(searchParams.get('from_date')),
                  to_date: Number(searchParams.get('to_date'))
                }}
                data={data?.data?.rows}
                height="746px"
                fetchFunction={getKeywordsFunc}
              />
            </div>
          </Col>
        ) : (
          <>
            <Col xxl={12}>
              <AgGridSkeleton
                height="696px"
                settings={{
                  pagination: true,
                  fullscreen: true,
                  changeHeight: true,
                  resetGrid: true,
                  saveGrid: false,
                  sideBar: true
                }}
              />
            </Col>
          </>
        )}
      </Row>
    </>
  );
};

export default KeywordsPage;
